import React, { useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { IconProvider } from '@/components/ui';
import { DialogField } from '@/lib/stores/dialogStore';
import { TextAreaElement } from '@/components/wizards/wizard-elements/TextAreaElement';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/DefaultSelectDropdown';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  getCachedStringsItems,
  updateTranslationLine,
  createTranslationLine,
} from '@/lib/stores/stringsStore';
import type { StringsItem } from '@/lib/stores/stringsStore';
import { DialogFieldType, DIALOG_FIELD_TYPES } from './types';
import { useDisplayLanguageOptional, resolveStringDisplayText } from '@/contexts/DisplayLanguageContext';
import { usePlatformTranslation } from '@/hooks/usePlatformTranslation';
import {
  SourceContextPanel,
  SourceTextSection,
} from '@/components/wizards/shared/source-context-panel';
import { getCachedTypes } from '@/lib/stores/typesStore';
import { getCachedObjects } from '@/lib/stores/objectsStore';

// ============================================================================
// TRANSLATION SAVE UTILITY
// ============================================================================

/**
 * Save translated text to the TMS for a specific language.
 * Creates a new translation line if none exists, or updates the existing one.
 */
async function saveTranslationToTMS(
  stringId: string,
  languageCode: string,
  text: string,
  linkedStringItem: StringsItem | null,
  createdTranslationIdRef: React.MutableRefObject<string | null>,
): Promise<void> {
  try {
    // Check for an existing translation (from loaded data or from a previous create in this session)
    const existingTranslation = linkedStringItem?.translations?.find(
      (t) => t.language_code === languageCode
    );
    const translationId = existingTranslation?.id || createdTranslationIdRef.current;

    if (translationId) {
      // Update existing translation
      await updateTranslationLine(translationId, {
        language_code: languageCode,
        translated_text: text,
        approved: false,
      });
    } else {
      // Create new translation
      const result = await createTranslationLine({
        dialogue_line_id: stringId,
        language_code: languageCode,
        translated_text: text,
        approved: false,
      });
      if (result?.id) {
        createdTranslationIdRef.current = result.id;
      }
    }
  } catch (error) {
    console.error('[saveTranslationToTMS] Failed to save translation:', error);
  }
}

// ============================================================================
// HOOKS
// ============================================================================

interface BaseFieldEditorProps {
  field: DialogField;
  fieldIndex: number;
  sectionIndex: number;
  dialogTranslatable: boolean;
  effectiveGameId?: string;
  onUpdateField: (sectionIndex: number, fieldId: string, updates: Partial<DialogField>) => void;
  onRemoveField: (sectionIndex: number, fieldId: string) => void;
  children?: ReactNode;
  connectionContent?: ReactNode;
}

/**
 * Hook for managing TMS string loading and display text state.
 * When a display language is selected, shows the translation for that language,
 * or "no translation available" if none exists.
 *
 * Enhanced to also return source text, linked string item, and translation mode info.
 */
export const useFieldTextState = (
  field: DialogField,
  effectiveGameId?: string
) => {
  const [locaKey, setLocaKey] = useState<string | null>(null);
  const [displayText, setDisplayText] = useState<string>(field.text);
  const [sourceText, setSourceText] = useState<string>(field.text);
  const [linkedStringItem, setLinkedStringItem] = useState<StringsItem | null>(null);
  const displayLanguageCtx = useDisplayLanguageOptional();
  const displayLanguage = displayLanguageCtx?.displayLanguage ?? null;
  const { t } = usePlatformTranslation();
  const noTranslationLabel = t('platform.TMS.no_translation');

  /** True when editing a translation (non-source language selected AND field is linked to TMS) */
  const isTranslationMode = displayLanguage !== null && !!field.string_id;

  // Load TMS string data when field has a string_id
  useEffect(() => {
    if (field.string_id && effectiveGameId) {
      const strings = getCachedStringsItems(effectiveGameId);
      const linkedString = strings.find(s => s.id === field.string_id);
      if (linkedString) {
        setLocaKey(linkedString.abbrev);
        setSourceText(linkedString.source_text);
        setLinkedStringItem(linkedString);
        const resolved = resolveStringDisplayText(linkedString, displayLanguage, noTranslationLabel);
        setDisplayText(resolved.text || field.text);
      } else {
        setLocaKey(field.string_id);
        setSourceText(field.text);
        setDisplayText(field.text);
        setLinkedStringItem(null);
      }
    } else {
      setLocaKey(null);
      setSourceText(field.text);
      setDisplayText(field.text);
      setLinkedStringItem(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [field.string_id, field.id, effectiveGameId, displayLanguage]);

  return {
    locaKey,
    displayText,
    setDisplayText,
    sourceText,
    linkedStringItem,
    isTranslationMode,
    displayLanguage,
  };
};

/**
 * Configuration for translation-aware debounced updates.
 */
interface TranslationModeConfig {
  isTranslationMode: boolean;
  stringId?: string | null;
  displayLanguage: string | null;
  linkedStringItem: StringsItem | null;
}

/**
 * Hook for debounced field text updates.
 * When translationConfig is provided and in translation mode,
 * saves to the TMS translation line instead of updating field.text.
 */
export const useDebouncedFieldUpdate = (
  onUpdateField: (sectionIndex: number, fieldId: string, updates: Partial<DialogField>) => void,
  sectionIndex: number,
  fieldId: string,
  translationConfig?: TranslationModeConfig,
) => {
  const debounceTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const createdTranslationIdRef = useRef<string | null>(null);

  // Reset created translation ID when language or string changes
  useEffect(() => {
    createdTranslationIdRef.current = null;
  }, [translationConfig?.displayLanguage, translationConfig?.stringId]);

  const debouncedUpdateField = useCallback((value: string) => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    if (
      translationConfig?.isTranslationMode &&
      translationConfig.stringId &&
      translationConfig.displayLanguage
    ) {
      // Translation mode: save to TMS with longer debounce (API call)
      debounceTimeoutRef.current = setTimeout(() => {
        saveTranslationToTMS(
          translationConfig.stringId!,
          translationConfig.displayLanguage!,
          value,
          translationConfig.linkedStringItem,
          createdTranslationIdRef,
        );
      }, 800);
    } else {
      // Source mode: update field text locally
      debounceTimeoutRef.current = setTimeout(() => {
        onUpdateField(sectionIndex, fieldId, { text: value });
      }, 300);
    }
  }, [
    onUpdateField, sectionIndex, fieldId,
    translationConfig?.isTranslationMode,
    translationConfig?.stringId,
    translationConfig?.displayLanguage,
    translationConfig?.linkedStringItem,
  ]);

  /**
   * Immediately flush any pending update (e.g. on blur).
   * @param currentText - The current text value to save
   */
  const flushUpdate = useCallback((currentText: string) => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
      debounceTimeoutRef.current = null;
    }

    if (
      translationConfig?.isTranslationMode &&
      translationConfig.stringId &&
      translationConfig.displayLanguage
    ) {
      saveTranslationToTMS(
        translationConfig.stringId,
        translationConfig.displayLanguage,
        currentText,
        translationConfig.linkedStringItem,
        createdTranslationIdRef,
      );
    } else {
      onUpdateField(sectionIndex, fieldId, { text: currentText });
    }
  }, [
    onUpdateField, sectionIndex, fieldId,
    translationConfig?.isTranslationMode,
    translationConfig?.stringId,
    translationConfig?.displayLanguage,
    translationConfig?.linkedStringItem,
  ]);

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  return { debouncedUpdateField, flushUpdate, debounceTimeoutRef };
};

/**
 * High-level convenience hook that combines useFieldTextState + useDebouncedFieldUpdate
 * into translation-aware text change / blur handlers.
 *
 * Use this in field editors to replace the manual composition of the lower-level hooks.
 *
 * @example
 * ```tsx
 * const { locaKey, displayText, sourceText, isTranslationMode, handleTextChange, handleTextBlur }
 *   = useFieldEditingHandlers(field, effectiveGameId, onUpdateField, sectionIndex);
 * ```
 */
export const useFieldEditingHandlers = (
  field: DialogField,
  effectiveGameId: string | undefined,
  onUpdateField: (sectionIndex: number, fieldId: string, updates: Partial<DialogField>) => void,
  sectionIndex: number,
) => {
  const {
    locaKey,
    displayText,
    setDisplayText,
    sourceText,
    linkedStringItem,
    isTranslationMode,
    displayLanguage,
  } = useFieldTextState(field, effectiveGameId);

  const { debouncedUpdateField, flushUpdate } = useDebouncedFieldUpdate(
    onUpdateField,
    sectionIndex,
    field.id,
    {
      isTranslationMode,
      stringId: field.string_id,
      displayLanguage,
      linkedStringItem,
    },
  );

  /** Handle text change — updates local state immediately, debounces the save */
  const handleTextChange = useCallback((value: string) => {
    setDisplayText(value);
    debouncedUpdateField(value);
  }, [debouncedUpdateField, setDisplayText]);

  /** Handle text blur — flushes any pending debounced update */
  const handleTextBlur = useCallback(() => {
    flushUpdate(displayText);
  }, [flushUpdate, displayText]);

  return {
    locaKey,
    displayText,
    sourceText,
    isTranslationMode,
    handleTextChange,
    handleTextBlur,
  };
};

// ============================================================================
// BASE FIELD EDITOR COMPONENT
// ============================================================================

/**
 * BaseFieldEditor provides the common UI structure and functionality
 * for all dialog field types
 */
export const BaseFieldEditor: React.FC<BaseFieldEditorProps> = ({
  field,
  fieldIndex,
  sectionIndex,
  dialogTranslatable,
  effectiveGameId,
  onUpdateField,
  onRemoveField,
  children,
  connectionContent,
}) => {
  const { locaKey, displayText, setDisplayText } = useFieldTextState(field, effectiveGameId);
  const { debouncedUpdateField, debounceTimeoutRef } = useDebouncedFieldUpdate(
    onUpdateField,
    sectionIndex,
    field.id
  );

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: field.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.3 : 1,
  };

  // Handle field text update - local state updates immediately, parent update is debounced
  const handleFieldTextChange = useCallback((value: string) => {
    setDisplayText(value);
    debouncedUpdateField(value);
  }, [debouncedUpdateField, setDisplayText]);

  // Flush debounced updates on blur to ensure data is saved
  const handleFieldTextBlur = useCallback(() => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
      debounceTimeoutRef.current = null;
    }
    onUpdateField(sectionIndex, field.id, { text: displayText });
  }, [onUpdateField, sectionIndex, field.id, displayText, debounceTimeoutRef]);

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-start gap-2 p-2 border border-gray-200 rounded-sm bg-white"
    >
      {/* Drag Handle */}
      <button
        {...attributes}
        {...listeners}
        className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600 pt-2"
        type="button"
      >
        <IconProvider iconstring="fa-grip-vertical" size={14} />
      </button>

      {/* Field Content */}
      <div className="flex-1 items-center justify-center space-y-2">
        {children}
      </div>

      {/* Field Type & Buttons */}
      <div className="flex items-center gap-1">
        {/* Connect Field Button with Popover */}
        {connectionContent && (
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="halfghost"
                size="sm"
                className="px-2 text-xs w-8 h-8"
                type="button"
              >
                <IconProvider
                  iconstring="fa-circle-nodes"
                  size={14}
                  color="currentColor"
                  className="hover:opacity-80 transition-colors"
                />
              </Button>
            </PopoverTrigger>
            <PopoverContent 
              className="w-80 p-3"
              align="end"
              side="left"
            >
              {connectionContent}
            </PopoverContent>
          </Popover>
        )}
        
        {/* Remove Field Button */}
        <Button
          variant="halfghost"
          size="sm"
          onClick={() => onRemoveField(sectionIndex, field.id)}
          className="px-2 text-xs w-8 h-8"
          type="button"
        >
          <IconProvider
            iconstring="fa-xmark"
            size={14}
            color="currentColor"
            className="hover:opacity-80 transition-colors"
          />
        </Button>
      </div>
    </div>
  );
};

// ============================================================================
// FIELD TEXT INPUT COMPONENT
// ============================================================================

/**
 * Reusable field text input component with TMS integration and
 * floating source context panel for translation mode.
 */
interface FieldTextInputProps {
  field: DialogField;
  displayText: string;
  locaKey: string | null;
  /** Source text to show in the floating panel (when in translation mode) */
  sourceText?: string;
  /** Whether the field is in translation mode (non-source language selected) */
  isTranslationMode?: boolean;
  dialogTranslatable: boolean;
  onTextChange: (value: string) => void;
  onTextBlur: () => void;
  onTypeChange: (type: DialogFieldType) => void;
  placeholder?: string;
  /** Game ID used to resolve variable reference types & objects for {{ }} autocomplete */
  gameId?: string;
}

export const FieldTextInput: React.FC<FieldTextInputProps> = ({
  field,
  displayText,
  locaKey,
  sourceText,
  isTranslationMode = false,
  dialogTranslatable,
  onTextChange,
  onTextBlur,
  onTypeChange,
  placeholder,
  gameId,
}) => {
  const [isFocused, setIsFocused] = useState(false);

  // Resolve types & objects for variable reference autocomplete
  const variableTypes = gameId ? getCachedTypes(gameId) : [];
  const variableObjects = gameId ? getCachedObjects(gameId) : [];

  const getPlaceholder = () => {
    if (placeholder) return placeholder;
    switch (field.type) {
      case 'question':
        return 'Enter question text...';
      case 'external-dialog':
        return 'Enter dialog jump description (optional)...';
      case 'end-text': // Legacy type, same as end-dialog
      case 'end-dialog':
        return 'Enter ending dialog...';
      case 'context':
        return 'Enter context text...';
      default:
        return 'Enter dialog text...';
    }
  };

  /** Show the source context panel when focused in translation mode */
  const showSourcePanel = isTranslationMode && isFocused && !!sourceText;

  /**
   * Track focus on the wrapper div. Uses event delegation so that
   * focus/blur events from the contenteditable inside bubble up.
   */
  const handleWrapperFocus = useCallback(() => {
    setIsFocused(true);
  }, []);

  const handleWrapperBlur = useCallback((e: React.FocusEvent<HTMLDivElement>) => {
    // Only unfocus if the new focus target is outside this wrapper
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setIsFocused(false);
    }
  }, []);

  return (
    <div className="flex items-start gap-2">
      <div className="flex items-center gap-1 ">
        {/* Field Type Dropdown */}
        <Select
          value={field.type}
          onValueChange={(value: DialogFieldType) => {
            console.log(`Field type dropdown changed to: ${value} for field ${field.id}`);
            onTypeChange(value);
          }}
        >
          <SelectTrigger className="w-32 h-8 text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {DIALOG_FIELD_TYPES.map(({ value, label }) => (
              <SelectItem key={value} value={value}>
                {label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div
        className="flex-1"
        onFocus={handleWrapperFocus}
        onBlur={handleWrapperBlur}
      >
        {locaKey ? (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div>
                  <TextAreaElement
                    value={displayText}
                    onChange={onTextChange}
                    onBlur={onTextBlur}
                    placeholder={getPlaceholder()}
                    rows={1}
                    className="border-none bg-transparent"
                    enableBubbleMenu={true}
                    enableVariableReferences={true}
                    variableTypes={variableTypes}
                    variableObjects={variableObjects}
                  />
                </div>
              </TooltipTrigger>
              <TooltipContent side="top" className="font-mono text-xs">
                <div className="flex items-center gap-1.5">
                  <IconProvider iconstring="fa-link" size={12} />
                  <span>{locaKey}</span>
                </div>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ) : (
          <TextAreaElement
            value={displayText}
            onChange={onTextChange}
            onBlur={onTextBlur}
            placeholder={getPlaceholder()}
            rows={1}
            enableBubbleMenu={true}
            enableVariableReferences={true}
            variableTypes={variableTypes}
            variableObjects={variableObjects}
          />
        )}

        {/* Floating source context panel — visible when editing a translation */}
        <SourceContextPanel isVisible={showSourcePanel}>
          <SourceTextSection sourceText={sourceText || ''} locaKey={locaKey} />
        </SourceContextPanel>
      </div>
    </div>
  );
};
